import { type ReactElement, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteProjects, getRouteSignIn } from "@/zapi-frontend/shared/const/router";
import { ERoleAccessType } from "@libs/shared-permissions";
import { ERoleAccess } from "../config/routeConfig";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";
// import { ImpersonationSessionWidget } from "@/frontend/components/ImpersonationThing/ImpersonationThing";
import styles from "./ProtectedRoute.module.scss";
import { LoadingSpinner } from "@libs/zapi-ui/custom";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { Header } from "@/zapi-frontend/widgets/Header";
import { UserMenu } from "@/zapi-frontend/widgets/UserMenu";
import { CustomSnippetsBanner } from "@/zapi-frontend/widgets/CustomSnippetsBanner";
import { PinnedPromptRevisionBanner } from "@/zapi-frontend/widgets/PinnedPromptRevisionBanner";
import { ReleaseBanner } from "@/zapi-frontend/widgets/ReleaseBanner";
import { PasswordExpiryBanner } from "@/zapi-frontend/widgets/PasswordExpiryBanner/ui/PasswordExpiryBanner";
import { useFeatureFlags } from "@/zapi-frontend/shared/hooks/useFeatureFlag";

type TProtectedRouteProps = {
  children: ReactElement;
  roleAccess?: ERoleAccessType;
};

export function ProtectedRoute({ children, roleAccess }: TProtectedRouteProps) {
  const { navigate } = useHistoricalNavigate();
  const { data: isAuth, isLoading, me } = useAuthenticated();
  useFeatureFlags();
  const { pathname, search } = useLocation();

  const isClientOnAdminPage = roleAccess === ERoleAccess.CLIENT_ADMIN && me?.role !== ERoleAccess.CLIENT_ADMIN;

  useEffect(() => {
    if (!isAuth && !isLoading) {
      if (pathname.includes("/project/")) {
        navigate(getRouteSignIn(), { replace: true });
        return;
      }

      const nextNav = pathname + search;
      navigate(getRouteSignIn() + `?next=${encodeURIComponent(nextNav)}`);
    }
  }, [isAuth, pathname, search, navigate, isLoading]);

  useEffect(() => {
    if (isAuth && isClientOnAdminPage) {
      navigate(getRouteProjects());
    }
  }, [isAuth, isClientOnAdminPage, navigate]);

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuth || isClientOnAdminPage) {
    return null;
  }

  const isPortfolioHome = pathname === "/";

  return (
    <div
      className={
        isPortfolioHome
          ? "flex h-screen flex-col overflow-hidden bg-[#f9fafb]"
          : "h-full bg-zinc-100 laptop:min-h-screen"
      }
    >
      <Header slot={<UserMenu />} />
      {!isPortfolioHome && <ReleaseBanner />}
      <CustomSnippetsBanner />
      <PinnedPromptRevisionBanner />
      <PasswordExpiryBanner />
      {children}
      {/*{userData?.impersonatorId ? <ImpersonationSessionWidget email={userData?.email} refetch={refetch} /> : null}*/}
    </div>
  );
}
